/**
 * G11 "This fortnight" engagement widget. Ported from
 * `frontend/src/components/dashboard/engagement-widget.tsx`.
 *
 * Surfaces upcoming birthdays + work-anniversaries inside a 14-day
 * window via GET /api/v1/engagement/upcoming?window_days=14.
 *
 * Behaviour:
 *  - Loading  -> 3-row skeleton.
 *  - Error    -> silent (engagement is decorative, not load-bearing).
 *  - Empty    -> small "No upcoming..." line.
 *  - Up to 5 items, grouped as Birthdays first then Anniversaries.
 */

import { queryOptions, useQuery } from "@tanstack/react-query";
import { Cake, PartyPopper } from "lucide-react";
import type { ReactNode } from "react";
import { engagementApi } from "@/lib/api";
import { useFeatures } from "@/lib/auth";
import type { UpcomingItem } from "@/lib/api/engagement";
import { upcomingDisplayName } from "@/lib/engagement-format";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

const WINDOW_DAYS = 14;
const MAX_ITEMS = 5;

/**
 * Shared query definition — the single source for this query's key + fn.
 * The Me-home route loader (app.$tenantSlug.index.tsx) prefetches with
 * the SAME options so hover-preload warms exactly this cache entry (A9).
 */
export const engagementUpcomingQueryOptions = queryOptions({
  queryKey: ["engagement", "upcoming", WINDOW_DAYS] as const,
  queryFn: ({ signal }) => engagementApi.getUpcoming(WINDOW_DAYS, signal),
  // staleTime: 5 min - birthdays don't change minute-to-minute and
  // the widget should not refetch on every dashboard re-mount.
  staleTime: 5 * 60 * 1000,
  retry: false,
});

/** Whole-day distance from today to a YYYY-MM-DD date, signed
 *  (negative for past dates - though the backend doesn't emit those).
 *  Local-time midnight on both sides to avoid DST and tz drift. */
function dayDistance(dateStr: string): number {
  const [y, m, d] = dateStr.split("-").map((n) => Number.parseInt(n, 10));
  if (!y || !m || !d) return 0;
  const target = new Date(y, m - 1, d);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round(
    (target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24),
  );
}

function relativeLabel(dateStr: string): string {
  const days = dayDistance(dateStr);
  if (days <= 0) return "today";
  if (days === 1) return "tomorrow";
  return `in ${days} days`;
}

function Row({ item }: { item: UpcomingItem }) {
  const Icon = item.kind === "birthday" ? Cake : PartyPopper;
  const detail =
    item.kind === "anniversary" && item.years_for_anniversary
      ? `${item.years_for_anniversary}-yr work anniversary`
      : "Birthday";
  return (
    <li className="flex items-center gap-3 py-1.5">
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-accent text-accent-foreground">
        <Icon className="h-3.5 w-3.5" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-foreground">
          {upcomingDisplayName(item)}
        </p>
        <p className="truncate text-xs text-muted-foreground">{detail}</p>
      </div>
      <span className="shrink-0 text-xs text-muted-foreground tabular-nums">
        {relativeLabel(item.date_in_window)}
      </span>
    </li>
  );
}

function HeaderShell({ children }: { children: ReactNode }) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-md bg-accent text-accent-foreground">
            <PartyPopper className="h-4 w-4" />
          </span>
          <CardTitle className="text-base">This Fortnight</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="pt-0">{children}</CardContent>
    </Card>
  );
}

export function EngagementWidget() {
  // POSH only (migration 162): birthdays/anniversaries are not on the plan.
  const platform = useFeatures().has("hr_platform");
  const query = useQuery({ ...engagementUpcomingQueryOptions, enabled: platform });

  if (!platform) return null;

  if (query.isError) {
    // Engagement is decorative - hide silently rather than show an
    // error stripe on the dashboard home.
    return null;
  }

  if (query.isPending) {
    return (
      <HeaderShell>
        <div className="space-y-2">
          <Skeleton className="h-7 w-full" />
          <Skeleton className="h-7 w-full" />
          <Skeleton className="h-7 w-3/4" />
        </div>
      </HeaderShell>
    );
  }

  const items = query.data?.items ?? [];
  if (items.length === 0) {
    return (
      <HeaderShell>
        <p className="text-xs text-muted-foreground">
          No upcoming birthdays or anniversaries in the next 2 weeks.
        </p>
      </HeaderShell>
    );
  }

  // Group: birthdays first, then anniversaries, then truncate to 5.
  const birthdays = items.filter((i) => i.kind === "birthday");
  const anniversaries = items.filter((i) => i.kind === "anniversary");
  const ordered = [...birthdays, ...anniversaries].slice(0, MAX_ITEMS);

  // Where do the anniversaries start in the ordered list? Used to
  // insert a subtle group divider header.
  const annivStart = ordered.findIndex((i) => i.kind === "anniversary");

  return (
    <HeaderShell>
      <ul className="divide-y">
        {ordered.map((item, idx) => (
          <div
            key={`${item.kind}-${item.employee_id}-${item.date_in_window}`}
          >
            {idx === 0 && birthdays.length > 0 && (
              <p className="pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Birthdays
              </p>
            )}
            {annivStart >= 0 && idx === annivStart && (
              <p className="pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Work anniversaries
              </p>
            )}
            <Row item={item} />
          </div>
        ))}
      </ul>
    </HeaderShell>
  );
}
